import { Button, useDisclosure } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { ShieldingFailedModal } from "../components/shielding/ShieldingFailedModal";
import { ShieldingForm } from "../components/shielding/ShieldingForm";
import { ShieldingReviewModal } from "../components/shielding/ShieldingReviewModal";
import { ShieldingSuccessModal } from "../components/shielding/ShieldingSuccessModal";
import { ShieldingSuccessToast } from "../components/shielding/ShieldingSuccessToast";
import { ShieldingWaitingModal } from "../components/shielding/ShieldingWaitingModal";
import { ShieldingWaitingToast } from "../components/shielding/ShieldingWaitingToast";
import type { ShieldingFormData, ShieldingTxFailed, ShieldingTxSuccess } from "../components/shielding/shieldingConfig";
import { TokensTable } from "../components/TokensTable";
import { useShieldingMutation } from "../mutations/aztec";
import { l2AccountAddressQuery } from "../queries/aztec";

export default function ShieldingRoute() {
  const { data: l2AccountAddress } = useQuery(l2AccountAddressQuery);

  const shieldingFormDataRef = useRef<ShieldingFormData | null>(null);
  // Shielding Tx result
  const shieldingSuccessRef = useRef<ShieldingTxSuccess | null>(null);
  const shieldingFailedRef = useRef<ShieldingTxFailed | null>(null);
  const [pendingTxHash, setPendingTxHash] = useState<string | undefined>();

  const reviewModalDisclosure = useDisclosure();
  const [isMinimized, setMinimized] = useState(false);

  const shieldingMutation = useShieldingMutation({
    onSuccess: (shieldingSuccess) => {
      shieldingSuccessRef.current = shieldingSuccess;
      shieldingFailedRef.current = null;
      setPendingTxHash(undefined);
    },
    onError: (shieldingFailed) => {
      shieldingFailedRef.current = shieldingFailed;
      shieldingSuccessRef.current = null;
      setPendingTxHash(undefined);
    },
    onTxSubmitted: (txHash) => {
      setPendingTxHash(txHash);
    },
  });

  function onReview(shieldingFormData: ShieldingFormData) {
    shieldingFormDataRef.current = shieldingFormData;
    reviewModalDisclosure.onOpen();
  }

  function onShield() {
    if (!shieldingFormDataRef.current) {
      throw new Error("Shielding form data is not valid");
    }

    reviewModalDisclosure.onClose();
    shieldingMutation.mutate(shieldingFormDataRef.current);
  }

  if (!l2AccountAddress) {
    return (
      <div className="flex flex-col items-center gap-4 max-w-screen-xl mx-auto mt-8 p-4">
        Your Aztec Wallet is not connected.
        <Button
          fullWidth={false}
          size="lg"
          color="default"
          radius="full"
          className="font-semibold h-16"
          onPress={() => {
            document.getElementById("connect-wallet-btn")?.click();
          }}
        >
          Connect Wallet
        </Button>
      </div>
    );
  }

  const shouldShowWaiting = !reviewModalDisclosure.isOpen && shieldingMutation.status === "pending";
  const shouldShowSuccess = shieldingMutation.status === "success";
  const shouldShowFailed = shieldingMutation.status === "error";

  return (
    <div className="max-w-screen-xl mx-auto my-12 grid grid-cols-12 gap-x-4 gap-y-8">
      <div className="flex flex-col gap-4 col-span-6">
        <h1 className="font-['Krona_One'] text-4xl">Shielding</h1>

        <p className="text-[#849AA9] text-sm font-medium">
          Shielding is the process during which the balance of your token, which is public, becomes private, as a result
          of it. <span className="text-[#14DBC0] font-semibold">Learn more</span>
        </p>
      </div>

      <ShieldingForm l2AccountAddress={l2AccountAddress} onReview={onReview} />

      <TokensTable />

      <ShieldingReviewModal
        isOpen={reviewModalDisclosure.isOpen}
        onClose={reviewModalDisclosure.onClose}
        shieldingFormData={shieldingFormDataRef.current}
        onShield={onShield}
      />

      <ShieldingWaitingModal
        isOpen={shouldShowWaiting && !isMinimized}
        onClose={() => {
          setMinimized(true);
        }}
        txHash={pendingTxHash}
        tokenSymbol={shieldingFormDataRef.current?.tokenSymbol}
        shieldingMode={shieldingFormDataRef.current?.shieldingMode}
      />

      {shouldShowWaiting && isMinimized && (
        <ShieldingWaitingToast txHash={pendingTxHash} onDismiss={() => setMinimized(false)} />
      )}

      <ShieldingSuccessModal
        isOpen={shouldShowSuccess && !isMinimized}
        onClose={() => {
          shieldingMutation.reset();
        }}
        shieldingResult={shieldingSuccessRef.current}
      />

      {shouldShowSuccess && isMinimized && (
        <ShieldingSuccessToast
          amount={shieldingFormDataRef.current?.displayAmount}
          tokenSymbol={shieldingFormDataRef.current?.tokenSymbol}
          txHash={shieldingSuccessRef.current?.txHash}
          onDismiss={() => {
            shieldingMutation.reset();
            setMinimized(false);
          }}
        />
      )}

      <ShieldingFailedModal
        isOpen={shouldShowFailed}
        onClose={() => {
          shieldingMutation.reset();
        }}
        failedTxData={shieldingFailedRef.current}
      />
    </div>
  );
}
